import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Shield, Plus, Edit2, Trash2, Map, Gift, CreditCard, Users, Sparkles, MapPin, Zap, Camera, CheckCircle2, ArrowRightLeft, GripVertical, Copy, Download, Award, Ticket } from "lucide-react";
import { toast } from "sonner";
import LocationPickerMap from "../components/LocationPickerMap";
import ImageUploader from "../components/ImageUploader";
import RichTextEditor from "../components/RichTextEditor";
import CodesPanel from "./admin/CodesPanel";
import { DotRatingPicker } from "../components/RouteSpecs";

// Riga "Specifica" per il form route: label + hint + DotRatingPicker.
function SpecField({ label, hint, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="font-bold text-ink-800 text-sm leading-tight">{label}</div>
        {hint && <div className="text-[11px] text-ink-500">{hint}</div>}
      </div>
      <DotRatingPicker value={value} onChange={onChange} testId={testId}/>
    </div>
  );
}

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function StatTile({ label, value, icon: Icon }) {
  return (
    <Card className="card-treasure p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-gold-light flex items-center justify-center">
        <Icon className="text-gold-hover" size={22}/>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest font-bold text-ink-500">{label}</div>
        <div className="font-display font-extrabold text-2xl text-ink-900">{value}</div>
      </div>
    </Card>
  );
}

// ---------- ROUTES ----------
function RoutesPanel() {
  const [routes, setRoutes] = useState([]);
  const [pointCounts, setPointCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPoints, setShowPoints] = useState(null);
  const [showRewards, setShowRewards] = useState(null); // route doc per gestire i premi
  const blank = {
    title: "", description: "", image_url: "", city: "", duration_min: 60, price_eur: 0,
    status: "draft", default_wrong_quest_penalty: 50,
    spec_contesto_urbano: 0, spec_sfida_mentale: 0, spec_movimento: 0, spec_spirito_squadra: 0,
    spec_min_age: "",
    outro_message: "",
    final_quest_enabled: false,
    final_quest_description: "",
    final_quest_type: "multiple",
    final_quest_question: "",
    final_quest_options: ["", "", "", ""],
    final_quest_answer_index: 0,
    final_quest_answer: "",
    final_quest_points: 100,
  };
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get("/admin/routes");
    setRoutes(data);
    // fetch point counts for each route
    const counts = {};
    await Promise.all(
      data.map(async (r) => {
        try {
          const { data: pts } = await api.get(`/admin/routes/${r.route_id}/points`);
          counts[r.route_id] = pts.length;
        } catch {
          counts[r.route_id] = 0;
        }
      })
    );
    setPointCounts(counts);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const minAge = form.spec_min_age === "" || form.spec_min_age == null ? null : Number(form.spec_min_age);
      const payload = {
        ...form,
        duration_min: Number(form.duration_min),
        price_eur: Number(form.price_eur),
        default_wrong_quest_penalty: Number(form.default_wrong_quest_penalty ?? 50),
        spec_contesto_urbano: Number(form.spec_contesto_urbano || 0),
        spec_sfida_mentale: Number(form.spec_sfida_mentale || 0),
        spec_movimento: Number(form.spec_movimento || 0),
        spec_spirito_squadra: Number(form.spec_spirito_squadra || 0),
        spec_min_age: minAge,
        outro_message: form.outro_message || "",
        final_quest_enabled: !!form.final_quest_enabled,
        final_quest_description: form.final_quest_description || "",
        final_quest_type: form.final_quest_type || "multiple",
        final_quest_question: form.final_quest_question || "",
        final_quest_options: (form.final_quest_options || []).slice(0, 4),
        final_quest_answer_index: Number(form.final_quest_answer_index || 0),
        final_quest_answer: form.final_quest_answer || "",
        final_quest_points: Number(form.final_quest_points || 100),
      };
      let savedRoute;
      if (editing) {
        const { data } = await api.put(`/admin/routes/${editing}`, payload);
        savedRoute = data;
      } else {
        const { data } = await api.post("/admin/routes", payload);
        savedRoute = data;
      }
      setOpen(false); setEditing(null); setForm(blank);
      await load();
      toast.success("Salvato");
      // Apri subito il gestore tappe per i nuovi percorsi
      if (!editing && savedRoute) {
        setShowPoints(savedRoute);
        toast.info("Ora aggiungi le tappe del percorso", { duration: 4000 });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Cancellare "${r.title}"?`)) return;
    await api.delete(`/admin/routes/${r.route_id}`); await load();
  };

  const duplicate = async (r) => {
    if (!window.confirm(`Duplicare "${r.title}" con tutte le tappe?`)) return;
    try {
      const { data } = await api.post(`/admin/routes/${r.route_id}/duplicate`);
      toast.success(`Duplicato! ${data.points_copied} tappe copiate. Lo trovi in bozza.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore duplicazione");
    }
  };

  const exportDataset = async () => {
    try {
      const resp = await api.get("/admin/export/routes", { responseType: "blob" });
      // Estrai il filename dall'header Content-Disposition se presente
      const disp = resp.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(disp);
      const fname = m ? m[1] : `odismap-routes-${new Date().toISOString().slice(0,10)}.json`;
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Dataset scaricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore export");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between flex-wrap gap-2">
        <h2 className="font-display font-bold text-2xl text-ink-900">Percorsi</h2>
        <div className="flex gap-2">
          <button onClick={exportDataset} className="text-sm font-bold text-ocean bg-ocean-light hover:bg-ocean/20 rounded-2xl px-4 py-2 flex items-center gap-1 shadow-tactile" title="Scarica tutto il dataset (percorsi + tappe) in JSON" data-testid="admin-export-routes">
            <Download size={16}/> Esporta dataset
          </button>
          <button onClick={() => { setForm(blank); setEditing(null); setOpen(true); }} className="btn-tactile !py-2 !px-4" data-testid="admin-new-route">
            <Plus size={18} className="inline mr-1"/> Nuovo percorso
          </button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {routes.map((r) => {
          const count = pointCounts[r.route_id] ?? 0;
          return (
            <Card key={r.route_id} className="card-treasure p-4" data-testid={`admin-route-${r.route_id}`}>
              <div className="flex gap-3">
                <img
                  src={r.image_url}
                  alt=""
                  className="w-20 rounded-2xl bg-parchment-dark shrink-0"
                  style={{ aspectRatio: "4 / 5", objectFit: "cover" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-ink-900 truncate">{r.title}</div>
                  <div className="text-xs text-ink-500">{r.city} · {r.duration_min}min · €{r.price_eur}</div>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.status==="published"?"bg-forest text-white":r.status==="draft"?"bg-parchment-dark text-ink-700":"bg-ink-500/20 text-ink-700"}`}>{r.status}</span>
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${count===0?"bg-compass/15 text-compass":"bg-gold-light text-ink-800"}`}>
                      <MapPin size={10}/> {count} tappe{count===0 ? " — vuoto!" : ""}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-3">
                <button
                  onClick={() => setShowPoints(r)}
                  className="col-span-2 btn-tactile !py-2 !px-3 text-sm flex items-center justify-center gap-1"
                  data-testid={`admin-points-${r.route_id}`}
                >
                  <MapPin size={14}/> Tappe ({count})
                </button>
                <button
                  onClick={() => setShowRewards(r)}
                  className="col-span-2 !py-2 !px-3 text-sm font-bold text-ink-900 bg-gold hover:bg-gold-hover rounded-xl shadow-tactile flex items-center justify-center gap-1"
                  data-testid={`admin-rewards-${r.route_id}`}
                  title="Configura omaggi sbloccabili a fine percorso"
                >
                  <Gift size={14}/> Omaggi
                </button>
                <button onClick={() => {
                  // Idrata i default della final quest per rotte create prima di iter28:
                  // garantisce che `final_quest_type` sia sempre "multiple" o "single"
                  // (non undefined) e che `final_quest_options` abbia 4 slot per il map.
                  const existingOpts = Array.isArray(r.final_quest_options) ? r.final_quest_options : [];
                  const hydratedOpts = existingOpts.length >= 4
                    ? existingOpts.slice(0, 4)
                    : [...existingOpts, ...Array(4 - existingOpts.length).fill("")];
                  setEditing(r.route_id);
                  setForm({
                    ...r,
                    final_quest_type: r.final_quest_type || "multiple",
                    final_quest_options: hydratedOpts,
                    final_quest_answer_index: r.final_quest_answer_index ?? 0,
                    final_quest_answer: r.final_quest_answer || "",
                    final_quest_description: r.final_quest_description || "",
                    final_quest_question: r.final_quest_question || "",
                    final_quest_points: r.final_quest_points ?? 100,
                    outro_message: r.outro_message || "",
                  });
                  setOpen(true);
                }} className="col-span-2 text-xs font-bold text-ink-800 bg-parchment-dark hover:bg-gold-light rounded-xl py-2 flex items-center justify-center gap-1" data-testid={`admin-route-edit-${r.route_id}`}>
                  <Edit2 size={12}/> Modifica info
                </button>
                <button onClick={() => duplicate(r)} className="text-xs font-bold text-ocean bg-ocean-light hover:bg-ocean/20 rounded-xl py-2 flex items-center justify-center gap-1" title="Duplica con tutte le tappe" data-testid={`admin-route-duplicate-${r.route_id}`}>
                  <Copy size={12}/> Duplica
                </button>
                <button onClick={() => del(r)} className="text-xs font-bold text-compass bg-red-50 hover:bg-red-100 rounded-xl py-2 flex items-center justify-center gap-1" data-testid={`admin-route-delete-${r.route_id}`}>
                  <Trash2 size={12}/>
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifica" : "Nuovo"} percorso</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <Label>Titolo</Label><Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} data-testid="route-form-title"/>
            <Label>Descrizione della caccia al tesoro</Label>
            <RichTextEditor
              value={form.description || ""}
              onChange={(html) => setForm({ ...form, description: html })}
              testIdPrefix="route-desc"
            />
            <p className="text-[11px] text-ink-500 -mt-1">
              💡 Editor completo: titoli, grassetto, elenchi, citazioni, codice, link. Verrà mostrato nella scheda del percorso.
            </p>
            <Label>Immagine di copertina</Label>
            <ImageUploader
              value={form.image_url}
              onChange={(url) => setForm({ ...form, image_url: url })}
              category="routes"
              testIdPrefix="route-image"
            />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Città</Label><Input value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})}/></div>
              <div><Label>Durata (min)</Label><Input type="number" value={form.duration_min} onChange={(e)=>setForm({...form,duration_min:e.target.value})}/></div>
              <div><Label>Prezzo €</Label><Input type="number" step="0.01" value={form.price_eur} onChange={(e)=>setForm({...form,price_eur:e.target.value})}/></div>
              <div><Label>Stato</Label>
                <Select value={form.status} onValueChange={(v)=>setForm({...form,status:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Bozza</SelectItem>
                    <SelectItem value="published">Pubblicato</SelectItem>
                    <SelectItem value="archived">Archiviato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Label>Penalità default per quest fallita 3 volte <span className="text-[10px] text-ink-500 font-normal">(punti tolti per tappa)</span></Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={form.default_wrong_quest_penalty ?? 50}
              onChange={(e)=>setForm({...form,default_wrong_quest_penalty:e.target.value})}
              placeholder="50"
              data-testid="route-form-default-penalty"
            />

            {/* Specifiche caccia al tesoro: 4 rating 0-5 + età minima */}
            <div className="pt-3 border-t border-outline">
              <Label className="text-base font-display font-bold">🎯 Specifiche caccia al tesoro <span className="text-[10px] text-ink-500 font-normal">(visibili nella scheda del percorso)</span></Label>
              <div className="mt-2 p-3 rounded-2xl bg-parchment-dark border border-outline space-y-3">
                <SpecField label="Contesto urbano" hint="Ambiente di gioco"
                  value={form.spec_contesto_urbano || 0}
                  onChange={(v) => setForm({ ...form, spec_contesto_urbano: v })}
                  testId="route-spec-contesto-urbano"/>
                <SpecField label="Sfida mentale" hint="Quanto fa pensare"
                  value={form.spec_sfida_mentale || 0}
                  onChange={(v) => setForm({ ...form, spec_sfida_mentale: v })}
                  testId="route-spec-sfida-mentale"/>
                <SpecField label="Movimento" hint="Componente fisica"
                  value={form.spec_movimento || 0}
                  onChange={(v) => setForm({ ...form, spec_movimento: v })}
                  testId="route-spec-movimento"/>
                <SpecField label="Spirito di squadra" hint="Adatta al gruppo"
                  value={form.spec_spirito_squadra || 0}
                  onChange={(v) => setForm({ ...form, spec_spirito_squadra: v })}
                  testId="route-spec-spirito-squadra"/>
                <div className="pt-2 border-t border-outline">
                  <Label className="text-sm">Età minima consigliata <span className="text-[10px] text-ink-500 font-normal">(vuoto = non specificato)</span></Label>
                  <Input
                    type="number" min="0" max="99"
                    value={form.spec_min_age ?? ""}
                    onChange={(e) => setForm({ ...form, spec_min_age: e.target.value })}
                    placeholder="es. 6"
                    className="w-28"
                    data-testid="route-spec-min-age"
                  />
                </div>
              </div>
            </div>

            {/* Messaggio conclusivo + Quest finale del taccuino */}
            <div className="pt-3 border-t border-outline space-y-3" data-testid="route-final-section">
              <Label className="text-base font-display font-bold">📜 Messaggio conclusivo</Label>
              <p className="text-[11px] text-ink-500 -mt-1">
                Mostrato a fine caccia PRIMA del premio. Se è attiva la quest finale del taccuino, appare DOPO averla risolta.
              </p>
              <RichTextEditor
                value={form.outro_message || ""}
                onChange={(html) => setForm({ ...form, outro_message: html })}
                placeholder="Complimenti! Hai completato la caccia..."
                testIdPrefix="route-outro-message"
              />

              <Label className="text-base font-display font-bold flex items-center gap-2">🧭 Quest finale del taccuino <span className="text-[10px] text-ink-500 font-normal">(mostrata a fine caccia con gli indizi collezionati)</span></Label>
              <div className="p-3 rounded-2xl bg-parchment-dark border border-outline space-y-3" data-testid="route-final-quest-section">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] text-ink-500 flex-1">
                    Se abilitata, alla fine di TUTTE le tappe l'utente vede gli indizi del taccuino + una descrizione + una quest finale (risposta singola o scelta multipla).
                  </p>
                  <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!form.final_quest_enabled}
                      onChange={(e) => setForm({ ...form, final_quest_enabled: e.target.checked })}
                      className="w-5 h-5 accent-forest"
                      data-testid="route-final-quest-toggle"
                    />
                    <span className={`text-xs font-extrabold ${form.final_quest_enabled ? "text-forest" : "text-ink-700"}`}>
                      {form.final_quest_enabled ? "Attiva" : "Disattiva"}
                    </span>
                  </label>
                </div>

                {form.final_quest_enabled && (
                  <>
                    <div>
                      <Label className="text-xs">Descrizione (mostrata insieme agli indizi del taccuino)</Label>
                      <RichTextEditor
                        value={form.final_quest_description || ""}
                        onChange={(html) => setForm({ ...form, final_quest_description: html })}
                        placeholder="Ora che hai raccolto tutti gli indizi, prova a risolvere..."
                        testIdPrefix="route-final-quest-description"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Tipo quest finale</Label>
                      <Select value={form.final_quest_type || "multiple"} onValueChange={(v)=>setForm({...form,final_quest_type:v})}>
                        <SelectTrigger data-testid="route-final-quest-type"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Risposta singola</SelectItem>
                          <SelectItem value="multiple">Scelta multipla</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Domanda finale</Label>
                      <RichTextEditor
                        value={form.final_quest_question || ""}
                        onChange={(html) => setForm({ ...form, final_quest_question: html })}
                        placeholder="Es: qual è la parola nascosta negli indizi?"
                        testIdPrefix="route-final-quest-question"
                      />
                    </div>
                    {form.final_quest_type === "multiple" ? (
                      <div>
                        <Label className="text-xs">Opzioni di risposta (4 — l'utente sceglie 1)</Label>
                        <div className="space-y-2 mt-1">
                          {(form.final_quest_options || ["", "", "", ""]).slice(0, 4).map((opt, i) => (
                            <div key={`fq-opt-${i}`} className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setForm({ ...form, final_quest_answer_index: i })}
                                className={`w-7 h-7 rounded-full font-bold text-xs flex items-center justify-center shrink-0 ${
                                  Number(form.final_quest_answer_index) === i
                                    ? "bg-forest text-white"
                                    : "bg-white border-2 border-outline text-ink-700"
                                }`}
                                title="Seleziona come risposta corretta"
                                data-testid={`route-final-quest-correct-${i}`}
                              >
                                {Number(form.final_quest_answer_index) === i ? "✓" : i + 1}
                              </button>
                              <Input
                                value={opt}
                                onChange={(e) => {
                                  const cs = [...(form.final_quest_options || ["", "", "", ""])];
                                  cs[i] = e.target.value;
                                  setForm({ ...form, final_quest_options: cs });
                                }}
                                placeholder={`Opzione ${i + 1}`}
                                data-testid={`route-final-quest-option-${i}`}
                              />
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-ink-500 mt-1.5">
                          Clicca sul numero per marcare l'opzione corretta.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <Label className="text-xs">Risposta corretta <span className="text-[10px] text-ink-500 font-normal">(case-insensitive)</span></Label>
                        <Input
                          value={form.final_quest_answer || ""}
                          onChange={(e) => setForm({ ...form, final_quest_answer: e.target.value })}
                          placeholder="Es: taccuino"
                          data-testid="route-final-quest-answer"
                        />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">Punti bonus assegnati alla soluzione</Label>
                      <Input
                        type="number" min="0" max="1000"
                        value={form.final_quest_points ?? 100}
                        onChange={(e) => setForm({ ...form, final_quest_points: e.target.value })}
                        className="w-32"
                        data-testid="route-final-quest-points"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <button onClick={save} className="btn-tactile mt-3" data-testid="route-form-save">Salva</button>
        </DialogContent>
      </Dialog>

      <PointsManager route={showPoints} onClose={() => setShowPoints(null)} />
      <RouteRewardsManager route={showRewards} onClose={() => setShowRewards(null)} />
    </div>
  );
}

// ---------- ROUTE REWARDS (omaggi sbloccabili a fine percorso) ----------
function RouteRewardsManager({ route, onClose }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { min_percent: 80, title: "", message: "", gift_code: "", active: true };
  const [form, setForm] = useState(blank);

  const load = () => route && api.get(`/admin/routes/${route.route_id}/rewards`).then((r) => setItems(r.data));
  useEffect(() => { if (route) load(); }, [route?.route_id]);  // eslint-disable-line

  if (!route) return null;

  const save = async () => {
    try {
      const payload = {
        route_id: route.route_id,
        min_percent: Math.max(0, Math.min(100, Number(form.min_percent || 0))),
        title: (form.title || "").trim(),
        message: form.message || "",
        gift_code: (form.gift_code || "").trim(),
        active: !!form.active,
      };
      if (!payload.title) { toast.error("Inserisci un titolo"); return; }
      if (editing) await api.put(`/admin/route-rewards/${editing}`, payload);
      else await api.post("/admin/route-rewards", payload);
      setOpen(false); setEditing(null); setForm(blank);
      await load();
      toast.success("Omaggio salvato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Cancellare omaggio "${r.title}"?`)) return;
    await api.delete(`/admin/route-rewards/${r.reward_id}`);
    await load();
  };

  return (
    <Dialog open={!!route} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-route-rewards-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Gift size={20} className="text-compass"/> Omaggi di "{route.title}"
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-ink-700 mb-3">
          Configura gli omaggi che gli utenti sbloccano a fine percorso in base al <strong>% del punteggio massimo</strong> raggiunto.
          Ogni soglia raggiunta invia un'email automatica con titolo, descrizione e (opzionale) codice coupon.
        </p>
        <div className="flex justify-end mb-3">
          <button
            onClick={() => { setForm(blank); setEditing(null); setOpen(true); }}
            className="btn-tactile !py-2 !px-4"
            data-testid="admin-new-route-reward"
          >
            <Plus size={16} className="inline mr-1"/> Nuovo omaggio
          </button>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-8 px-4 bg-parchment-dark rounded-2xl border-2 border-dashed border-outline" data-testid="admin-no-route-rewards">
            <Award className="mx-auto text-ink-500 mb-2" size={32}/>
            <p className="text-ink-700 font-bold">Nessun omaggio configurato</p>
            <p className="text-sm text-ink-500 mt-1">Aggiungi soglie (es. 50%, 75%, 100%) per premiare gli utenti che completano il percorso.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((r) => (
              <Card key={r.reward_id} className="card-treasure p-3 flex items-start gap-3" data-testid={`admin-route-reward-${r.reward_id}`}>
                <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center shrink-0">
                  <span className="font-display font-extrabold text-ink-900">{r.min_percent}%</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-ink-900 truncate">{r.title}</div>
                  {r.message && <div className="text-xs text-ink-700 line-clamp-2 mt-0.5">{r.message}</div>}
                  {r.gift_code && (
                    <div className="mt-1 inline-block px-2 py-0.5 bg-gold-light border border-gold rounded-lg font-mono text-[11px] font-bold">
                      {r.gift_code}
                    </div>
                  )}
                  <div className="mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.active ? "bg-forest text-white" : "bg-parchment-dark text-ink-700"}`}>
                      {r.active ? "Attivo" : "Disattivo"}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => { setEditing(r.reward_id); setForm({ ...r }); setOpen(true); }}
                    className="text-gold-hover p-1"
                    data-testid={`admin-route-reward-edit-${r.reward_id}`}
                  >
                    <Edit2 size={16}/>
                  </button>
                  <button
                    onClick={() => del(r)}
                    className="text-compass p-1"
                    data-testid={`admin-route-reward-del-${r.reward_id}`}
                  >
                    <Trash2 size={16}/>
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-3xl max-w-md" data-testid="admin-route-reward-form-dialog">
            <DialogHeader><DialogTitle className="font-display">{editing ? "Modifica" : "Nuovo"} omaggio</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Soglia % completamento <span className="text-[10px] text-ink-500 font-normal">(es. 80 = sblocca chi ottiene almeno l'80% del punteggio max)</span></Label>
                <Input
                  type="number" min="0" max="100"
                  value={form.min_percent}
                  onChange={(e) => setForm({ ...form, min_percent: e.target.value })}
                  data-testid="admin-route-reward-min-percent"
                />
              </div>
              <div>
                <Label>Titolo</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es: Coupon sconto 10€" data-testid="admin-route-reward-title"/>
              </div>
              <div>
                <Label>Descrizione / contenuto email</Label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} placeholder="Es: Hai sbloccato uno sconto del 10% sui prodotti del nostro shop locale..." data-testid="admin-route-reward-message"/>
              </div>
              <div>
                <Label>Codice omaggio <span className="text-[10px] text-ink-500 font-normal">(opzionale)</span></Label>
                <Input value={form.gift_code} onChange={(e) => setForm({ ...form, gift_code: e.target.value })} placeholder="Es: ODISMAP10" data-testid="admin-route-reward-code"/>
              </div>
              <label className="flex items-center gap-2 text-sm font-bold text-ink-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="admin-route-reward-active"
                />
                Attivo (assegnato ai prossimi utenti che raggiungono la soglia)
              </label>
            </div>
            <button onClick={save} className="btn-tactile mt-3 w-full" data-testid="admin-route-reward-save">Salva omaggio</button>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

// Single sortable row inside PointsManager
function SortablePointRow({ p, onEdit, onMove, onDel }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.point_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <Card
      ref={setNodeRef}
      style={style}
      className="card-treasure p-3 flex items-center gap-3"
      data-testid={`admin-point-row-${p.point_id}`}
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="cursor-grab active:cursor-grabbing text-ink-500 hover:text-ink-800 p-1 -ml-1 touch-none"
        title="Trascina per riordinare"
        aria-label="Trascina per riordinare"
        data-testid={`admin-point-drag-${p.point_id}`}
      >
        <GripVertical size={18}/>
      </button>
      <div className="w-10 h-10 rounded-xl bg-gold flex items-center justify-center font-display font-bold">{p.order}</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-ink-800 truncate">{p.name}</div>
        <div className="text-xs text-ink-500">{p.lat.toFixed(4)}, {p.lng.toFixed(4)} · r={p.radius_m}m · {p.max_points}pt</div>
      </div>
      <button onClick={() => onEdit(p)} className="text-gold-hover" data-testid={`admin-point-edit-${p.point_id}`}><Edit2 size={16}/></button>
      <button onClick={() => onMove(p)} className="text-ocean" title="Sposta su un altro percorso" data-testid={`admin-point-move-${p.point_id}`}><ArrowRightLeft size={16}/></button>
      <button onClick={() => onDel(p)} className="text-compass" data-testid={`admin-point-del-${p.point_id}`}><Trash2 size={16}/></button>
    </Card>
  );
}

// ---------- POINTS ----------
function PointsManager({ route, onClose }) {
  const [points, setPoints] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = (route_id) => ({
    route_id, order: 1, name: "", lat: 41.9, lng: 12.5, radius_m: 10,
    clues: [{text:"",penalty:20}],
    proximity_question: "", proximity_options: ["", "", "", ""], proximity_answer_index: 0,
    quest_type: "single", quest_question: "", quest_options: [], quest_answer: "",
    main_quest_disabled: false,
    max_points: 100, description: "", image_url: "", wrong_quest_penalty: "",
    extra_quests: [],
  });
  const [form, setForm] = useState(blank(null));
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [movingPoint, setMovingPoint] = useState(null);  // {point_id, name}
  const [allRoutes, setAllRoutes] = useState([]);

  const openMoveDialog = async (p) => {
    try {
      const { data } = await api.get("/admin/routes");
      setAllRoutes(data.filter((r) => r.route_id !== route.route_id));
      setMovingPoint({ point_id: p.point_id, name: p.name });
    } catch (e) {
      toast.error("Impossibile caricare i percorsi");
    }
  };

  const movePointTo = async (target_route_id) => {
    if (!movingPoint) return;
    try {
      await api.post(`/admin/points/${movingPoint.point_id}/move`, { target_route_id });
      toast.success(`"${movingPoint.name}" spostata nell'altro percorso`);
      setMovingPoint(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore spostamento");
    }
  };

  // Drag & drop sensors (mouse, touch, keyboard)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = points.findIndex((p) => p.point_id === active.id);
    const newIndex = points.findIndex((p) => p.point_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(points, oldIndex, newIndex).map((p, i) => ({ ...p, order: i + 1 }));
    setPoints(reordered);  // optimistic UI
    try {
      await api.post("/admin/points/reorder", {
        route_id: route.route_id,
        point_ids: reordered.map((p) => p.point_id),
      });
    } catch (e) {
      toast.error("Errore riordino — ripristino l'ordine");
      await load();
    }
  };

  const uploadVideo = async (file) => {
    if (!editing) {
      toast.error("Prima salva la tappa, poi carica il video");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Video troppo grande (max 50MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploadingVideo(true);
    try {
      const { data } = await api.post(`/admin/points/${editing}/upload-video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, video_path: data.video_path, video_url: data.video_url }));
      toast.success("Video caricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload video");
    } finally {
      setUploadingVideo(false);
    }
  };

  const load = () => route && api.get(`/admin/routes/${route.route_id}/points`).then((r) => setPoints(r.data));
  useEffect(() => { if (route) { load(); } }, [route?.route_id]); // eslint-disable-line

  if (!route) return null;

  const save = async () => {
    try {
      // Normalizza proximity options: rimuovi vuote, normalizza l'indice
      const proxOptsRaw = form.proximity_options || [];
      const proxOpts = proxOptsRaw.map((s) => (s || "").trim()).filter(Boolean);
      let proxIdx = Number(form.proximity_answer_index ?? 0);
      if (proxOpts.length === 0) {
        proxIdx = 0;
      } else if (proxIdx < 0 || proxIdx >= proxOpts.length) {
        proxIdx = 0;
      }
      const payload = {
        ...form,
        order: Number(form.order), lat: Number(form.lat), lng: Number(form.lng),
        radius_m: Number(form.radius_m), max_points: Number(form.max_points),
        clues: form.clues.filter(c => c.text).map(c => ({text:c.text, penalty:Number(c.penalty||20)})),
        quest_options: form.quest_type === "multiple" ? form.quest_options.filter(Boolean) : [],
        wrong_quest_penalty: form.wrong_quest_penalty === "" || form.wrong_quest_penalty == null ? null : Number(form.wrong_quest_penalty),
        proximity_question: (form.proximity_question || "").trim(),
        proximity_options: proxOpts,
        proximity_answer_index: proxIdx,
        extra_quests: (form.extra_quests || []).map((q, i) => {
          const type = q.type === "single" ? "single" : "multiple";
          return {
            quest_id: q.quest_id || `eq_${Date.now()}_${i}`,
            order: i + 1,
            type,
            question: (q.question || "").trim(),
            options: type === "multiple" ? (q.options || []).map(o => (o || "").trim()).filter(Boolean) : [],
            answer_index: type === "multiple" ? Number(q.answer_index || 0) : 0,
            answer: type === "single" ? (q.answer || "").trim() : "",
            taccuino_enabled: !!q.taccuino_enabled,
            taccuino_value: q.taccuino_enabled ? (q.taccuino_value || "").trim() : "",
          };
        }).filter(q => q.question),
      };
      if (editing) await api.put(`/admin/points/${editing}`, payload);
      else await api.post("/admin/points", payload);
      setOpen(false); setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (p) => {
    if (!window.confirm("Cancellare punto?")) return;
    await api.delete(`/admin/points/${p.point_id}`); await load();
  };

  const createHereByGps = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalizzazione non disponibile sul dispositivo");
      return;
    }
    setGpsBusy(true);
    toast.info("📡 Rilevamento GPS in corso…", { duration: 2000 });

    const buildPayload = (lat, lng) => ({
      route_id: route.route_id,
      order: points.length + 1,
      name: `Tappa ${points.length + 1}`,
      lat,
      lng,
      radius_m: 5,
      max_points: 100,
      clues: [],
      quest_type: "single",
      quest_question: "",
      quest_options: [],
      quest_answer: "",
      main_quest_disabled: false,
      proximity_question: "",
      proximity_options: ["", "", "", ""],
      proximity_answer_index: 0,
      description: "",
      image_url: "",
      wrong_quest_penalty: "",
    });

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const accuracy = pos.coords.accuracy ?? 999;
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setGpsAccuracy(accuracy);

        // VALIDAZIONE RIGOROSA ≤10m
        if (accuracy > 10) {
          setGpsBusy(false);
          let title, body;
          if (accuracy > 50) {
            title = `❌ GPS troppo impreciso (±${Math.round(accuracy)}m) — richiesto ≤10m`;
            body = "Indoor o zone coperte non raggiungono mai 10m. Esci all'aperto in piazza/strada larga, cielo visibile, attendi 20-30s, poi riprova. Alternativa: usa coordinate manuali.";
          } else if (accuracy > 20) {
            title = `⚠️ GPS insufficiente (±${Math.round(accuracy)}m) — richiesto ≤10m`;
            body = "Spostati in uno spazio più aperto, allontanati da edifici, aspetta 10-20s e riprova.";
          } else {
            title = `⚠️ Quasi pronto (±${Math.round(accuracy)}m) — richiesto ≤10m`;
            body = "Il GPS si sta stabilizzando. Aspetta qualche secondo e riprova.";
          }
          toast.error(title, { description: body, duration: 8000 });
          // NON creare il punto, ma apri il dialog con la posizione catturata
          // e mostra l'indicatore di accuratezza nella mappa.
          setForm(buildPayload(lat, lng));
          setEditing(null);
          setOpen(true);
          return;
        }

        // ✅ ACCETTATO (≤10m)
        try {
          const payload = buildPayload(lat, lng);
          const { data } = await api.post("/admin/points", payload);
          toast.success(`✅🎯 Tappa creata con precisione ottima!`, {
            description: `GPS: ±${Math.round(accuracy)}m`,
            duration: 4000,
          });
          await load();
          setEditing(data.point_id || (await fetchLastPointId()));
          setForm({ ...payload, point_id: data.point_id });
          setOpen(true);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore creazione tappa");
        } finally {
          setGpsBusy(false);
        }
      },
      (err) => {
        setGpsBusy(false);
        if (err.code === 1) {
          toast.error("❌ Permesso geolocalizzazione negato", {
            description: "Abilita il GPS nelle impostazioni del browser.",
            duration: 5000,
          });
        } else if (err.code === 2) {
          toast.error("❌ Posizione non disponibile", {
            description: "Controlla che il GPS sia attivo, esci all'aperto e riprova.",
            duration: 5000,
          });
        } else if (err.code === 3) {
          toast.error("⏱️ Timeout GPS", {
            description: "Il GPS sta impiegando troppo tempo. Esci all'aperto e riprova.",
            duration: 5000,
          });
        } else {
          toast.error("❌ Errore GPS non specificato");
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  const fetchLastPointId = async () => {
    const { data } = await api.get(`/admin/routes/${route.route_id}/points`);
    return data[data.length - 1]?.point_id;
  };

  return (
    <Dialog open={!!route} onOpenChange={onClose}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Tappe di "{route.title}"</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-ink-700 mb-3">
          Una tappa è un punto sulla mappa che l'utente deve raggiungere. Puoi aggiungerla manualmente o usare la posizione GPS attuale (raggio 5m).
        </p>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          <button onClick={() => { setForm({...blank(route.route_id), order: points.length+1}); setEditing(null); setGpsAccuracy(null); setOpen(true); }} className="btn-tactile !py-3 flex items-center justify-center gap-2" data-testid="admin-new-point">
            <Plus size={18}/> Aggiungi nuova tappa
          </button>
          <button
            onClick={createHereByGps}
            disabled={gpsBusy}
            className="!py-3 px-4 rounded-2xl bg-forest text-white font-bold shadow-tactile hover:bg-forest/90 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="admin-new-point-gps"
            title="Richiede GPS ±10m — esci all'aperto"
          >
            <MapPin size={18}/> {gpsBusy ? "📡 Rilevamento GPS…" : "Crea tappa con GPS (richiede ±10m)"}
          </button>
        </div>
        {points.length === 0 && (
          <div className="text-center py-8 px-4 bg-parchment-dark rounded-2xl border-2 border-dashed border-outline">
            <MapPin className="mx-auto text-ink-500 mb-2" size={32}/>
            <p className="text-ink-700 font-bold">Nessuna tappa ancora</p>
            <p className="text-sm text-ink-500 mt-1">Aggiungi almeno 1 tappa per rendere il percorso giocabile.</p>
          </div>
        )}
        <div className="space-y-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={points.map((p) => p.point_id)} strategy={verticalListSortingStrategy}>
              {points.map((p) => (
                <SortablePointRow
                  key={p.point_id}
                  p={p}
                  onEdit={(pt) => {
                    setEditing(pt.point_id);
                    // Hydrate UI defaults: garantisce 4 slot per proximity_options
                    const proxOpts = (pt.proximity_options || []).slice();
                    while (proxOpts.length < 4) proxOpts.push("");
                    setForm({
                      ...pt,
                      proximity_question: pt.proximity_question || "",
                      proximity_options: proxOpts,
                      proximity_answer_index: pt.proximity_answer_index ?? 0,
                      extra_quests: (pt.extra_quests || []).map((q, i) => ({
                        quest_id: q.quest_id,
                        order: q.order || i + 1,
                        type: q.type === "single" ? "single" : "multiple",
                        question: q.question || "",
                        options: q.type === "multiple" ? ((q.options || []).slice().concat(["", "", "", ""]).slice(0, Math.max(4, (q.options || []).length))) : ["", "", "", ""],
                        answer_index: q.answer_index ?? 0,
                        answer: q.answer || "",
                        taccuino_enabled: !!q.taccuino_enabled,
                        taccuino_value: q.taccuino_value || "",
                      })),
                    });
                    setGpsAccuracy(null);
                    setOpen(true);
                  }}
                  onMove={openMoveDialog}
                  onDel={del}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setGpsAccuracy(null); }}>
          <DialogContent className="rounded-3xl max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">{editing ? "Modifica" : "Nuovo"} punto</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ordine</Label><Input type="number" value={form.order} onChange={(e)=>setForm({...form,order:e.target.value})}/></div>
                <div><Label>Nome</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></div>
                <div>
                  <Label>
                    Raggio (m)
                    <span className="text-[10px] text-ink-500 font-normal ml-1">(consigliato 10-15m)</span>
                  </Label>
                  <Input
                    type="number"
                    value={form.radius_m}
                    onChange={(e)=>setForm({...form,radius_m:e.target.value})}
                    data-testid="point-radius-input"
                  />
                  {Number(form.radius_m) > 30 && (
                    <p className="text-[11px] text-yellow-700 mt-1 font-bold" data-testid="point-radius-warning">
                      ⚠️ Raggio largo: meglio ridurlo per una validazione più precisa
                    </p>
                  )}
                </div>
                <div><Label>Punti max</Label><Input type="number" value={form.max_points} onChange={(e)=>setForm({...form,max_points:e.target.value})}/></div>
                <div className="col-span-2">
                  <Label>Penalità se quest fallita 3 volte <span className="text-[10px] text-ink-500 font-normal">(vuoto = usa default della rotta)</span></Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="usa default rotta"
                    value={form.wrong_quest_penalty ?? ""}
                    onChange={(e)=>setForm({...form,wrong_quest_penalty:e.target.value})}
                    data-testid="point-wrong-quest-penalty"
                  />
                </div>
              </div>

              <Label>Posizione sulla mappa</Label>
              <LocationPickerMap
                value={{ lat: form.lat, lng: form.lng }}
                onChange={({ lat, lng }) => setForm({ ...form, lat, lng })}
                radius_m={Number(form.radius_m) || 50}
                height={320}
                gpsAccuracy={gpsAccuracy}
                showAccuracyInfo={gpsAccuracy !== null}
              />
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-ink-500">Lat <span className="text-[10px]">(usa GPS ≤10m o coordinate precise)</span></Label><Input type="number" step="0.000001" value={form.lat} onChange={(e)=>setForm({...form,lat:e.target.value})} data-testid="point-lat-input"/></div>
                <div><Label className="text-xs text-ink-500">Lng <span className="text-[10px]">(usa GPS ≤10m o coordinate precise)</span></Label><Input type="number" step="0.000001" value={form.lng} onChange={(e)=>setForm({...form,lng:e.target.value})} data-testid="point-lng-input"/></div>
              </div>
              <Label>Indizi (WYSIWYG, con penalità 20 default)</Label>
              {form.clues.map((c, i) => (
                <div key={`clue-${i}`} className="space-y-2 p-3 rounded-2xl bg-parchment-dark border border-outline">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs uppercase tracking-widest font-bold text-ink-500">Indizio {i+1}</span>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-ink-500 !m-0">Penalità</Label>
                      <Input
                        type="number"
                        value={c.penalty}
                        onChange={(e)=>{const cs=[...form.clues];cs[i]={...c,penalty:e.target.value};setForm({...form,clues:cs});}}
                        className="w-20 h-9"
                        data-testid={`clue-${i}-penalty`}
                      />
                      {form.clues.length > 1 && (
                        <button
                          type="button"
                          onClick={()=>{const cs=form.clues.filter((_,idx)=>idx!==i);setForm({...form,clues:cs.length?cs:[{text:"",penalty:20}]});}}
                          className="text-xs text-compass font-bold hover:underline"
                          data-testid={`clue-${i}-remove`}
                        >Rimuovi</button>
                      )}
                    </div>
                  </div>
                  <RichTextEditor
                    value={c.text || ""}
                    onChange={(html)=>{const cs=[...form.clues];cs[i]={...c,text:html};setForm({...form,clues:cs});}}
                    placeholder={`Scrivi l'indizio ${i+1}...`}
                    testIdPrefix={`clue-${i}-text`}
                  />
                </div>
              ))}
              <button onClick={()=>setForm({...form,clues:[...form.clues,{text:"",penalty:20}]})} className="text-sm text-ocean font-bold" data-testid="add-clue-btn">+ Aggiungi indizio</button>

              <Label>Descrizione (mostrata + letta dopo lo sblocco)</Label>
              <RichTextEditor
                value={form.description || ""}
                onChange={(html) => setForm({ ...form, description: html })}
                testIdPrefix="point-description"
              />
              <p className="text-[11px] text-ink-500 -mt-1">
                💡 Usa l'editor per formattare: titoli, grassetto, elenchi, link. Il testo verrà letto ad alta voce ignorando la formattazione.
              </p>

              <Label>Immagine della tappa <span className="text-[10px] text-ink-500 font-normal">(mostrata all'utente quando arriva sul punto)</span></Label>
              <ImageUploader
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
                category="points"
                testIdPrefix="point-image"
              />

              <Label className="flex items-center gap-2">Video (max 50MB) {form.video_path && <span className="text-[10px] text-forest font-bold">✓ caricato</span>}</Label>
              <input
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])}
                disabled={!editing || uploadingVideo}
                className="block w-full text-sm text-ink-800 file:btn-tactile file:!py-2 file:!px-4 file:mr-3 disabled:opacity-50"
                data-testid="point-video-input"
              />
              {!editing && <p className="text-xs text-ink-500 italic">Salva la tappa per abilitare l'upload video</p>}
              {uploadingVideo && <p className="text-xs text-ocean font-bold">Caricamento in corso...</p>}
              {form.video_path && form.video_url && (
                <video src={form.video_url} controls className="w-full rounded-xl max-h-48 bg-black"/>
              )}

              <Label className="text-base font-display font-bold flex items-center gap-2">🎯 Quest "sei sul posto?" <span className="text-[10px] text-ink-500 font-normal">(scelta multipla)</span></Label>
              <div className="p-3 rounded-2xl bg-parchment-dark border border-outline space-y-3" data-testid="point-proximity-section">
                <div>
                  <Label className="text-xs">Domanda visibile in prossimità</Label>
                  <RichTextEditor
                    value={form.proximity_question || ""}
                    onChange={(html) => setForm({ ...form, proximity_question: html })}
                    placeholder="Es: di che colore è il portone della chiesa?"
                    testIdPrefix="point-proximity-question"
                  />
                </div>
                <div>
                  <Label className="text-xs">Opzioni di risposta (4 — l'utente sceglie 1)</Label>
                  <div className="space-y-2 mt-1">
                    {(form.proximity_options || []).slice(0, 4).map((opt, i) => (
                      <div key={`prox-opt-${i}`} className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, proximity_answer_index: i })}
                          className={`w-7 h-7 rounded-full font-bold text-xs flex items-center justify-center shrink-0 ${
                            Number(form.proximity_answer_index) === i
                              ? "bg-forest text-white"
                              : "bg-white border-2 border-outline text-ink-700"
                          }`}
                          title="Seleziona come risposta corretta"
                          data-testid={`point-proximity-correct-${i}`}
                        >
                          {Number(form.proximity_answer_index) === i ? "✓" : i + 1}
                        </button>
                        <Input
                          value={opt}
                          onChange={(e) => {
                            const cs = [...(form.proximity_options || ["", "", "", ""])];
                            cs[i] = e.target.value;
                            setForm({ ...form, proximity_options: cs });
                          }}
                          placeholder={`Opzione ${i + 1}`}
                          data-testid={`point-proximity-option-${i}`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-ink-500 mt-1.5">
                    Clicca sul numero per marcare l'opzione corretta.
                  </p>
                </div>
              </div>

              <div className={`p-3 rounded-2xl border-2 ${form.main_quest_disabled ? "bg-compass/10 border-compass" : "bg-white border-outline"}`} data-testid="main-quest-section">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <Label className="text-forest font-extrabold">Quest principale</Label>
                    <p className="text-[11px] text-ink-500 mt-0.5">
                      Disattiva la quest principale se hai già spostato la domanda nelle Quest extra sequenziali. I campi restano salvati come backup.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none" data-testid="main-quest-disable-toggle">
                    <input
                      type="checkbox"
                      checked={!!form.main_quest_disabled}
                      onChange={(e) => setForm({ ...form, main_quest_disabled: e.target.checked })}
                      className="w-5 h-5 accent-compass"
                      data-testid="main-quest-disable-checkbox"
                    />
                    <span className={`text-xs font-extrabold ${form.main_quest_disabled ? "text-compass" : "text-ink-700"}`}>
                      {form.main_quest_disabled ? "Disattivata" : "Disattiva quest"}
                    </span>
                  </label>
                </div>

                <div className={form.main_quest_disabled ? "opacity-50 pointer-events-none" : ""}>
                  <Label>Tipo quest</Label>
                  <Select value={form.quest_type} onValueChange={(v)=>setForm({...form,quest_type:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Risposta singola</SelectItem>
                      <SelectItem value="multiple">Scelta multipla</SelectItem>
                      <SelectItem value="observation">Osservazione</SelectItem>
                    </SelectContent>
                  </Select>
                  <Label>Domanda quest</Label>
                  <RichTextEditor
                    value={form.quest_question || ""}
                    onChange={(html) => setForm({ ...form, quest_question: html })}
                    placeholder="Es: quanti gradini ci sono?"
                    testIdPrefix="quest-question"
                  />
                  {form.quest_type === "multiple" && (
                    <>
                      <Label>Opzioni (una per riga)</Label>
                      <Textarea value={(form.quest_options||[]).join("\n")} onChange={(e)=>setForm({...form,quest_options:e.target.value.split("\n")})}/>
                    </>
                  )}
                  <Label>Risposta quest</Label><Input value={form.quest_answer} onChange={(e)=>setForm({...form,quest_answer:e.target.value})}/>
                </div>
                {form.main_quest_disabled && (
                  <p className="text-xs text-compass font-bold mt-2" data-testid="main-quest-disabled-warning">
                    ⚠️ La quest principale sarà saltata durante il gioco. Assicurati di aver copiato la domanda in una Quest extra qui sotto.
                  </p>
                )}
              </div>

              {/* Extra Quests (0..N) sequenziali dopo la quest principale */}
              <div className="pt-4 mt-4 border-t-2 border-outline">
                <div className="p-2.5 mb-3 rounded-xl bg-gold-light border border-outline">
                  <p className="text-sm text-ink-900 font-bold" data-testid="extra-quests-helper">
                    sposta la quest principale in queste quest
                  </p>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-forest font-extrabold">Quest extra sequenziali</Label>
                    <p className="text-[11px] text-ink-500 mt-0.5">Ogni quest extra ha la stessa UX della quest sul posto. Ordine: si susseguono dopo la quest principale.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newQ = {
                        quest_id: `eq_${Date.now()}_${(form.extra_quests || []).length}`,
                        order: (form.extra_quests || []).length + 1,
                        type: "multiple",
                        question: "",
                        options: ["", "", "", ""],
                        answer_index: 0,
                        answer: "",
                        taccuino_enabled: false,
                        taccuino_value: "",
                      };
                      setForm({ ...form, extra_quests: [...(form.extra_quests || []), newQ] });
                    }}
                    className="px-3 py-1.5 rounded-full bg-forest text-white text-xs font-bold hover:bg-forest-hover"
                    data-testid="add-extra-quest-btn"
                  >
                    + Aggiungi quest
                  </button>
                </div>
                {(form.extra_quests || []).map((q, qi) => (
                  <div key={q.quest_id || `eq-${qi}`} className="p-3 mt-2 rounded-2xl bg-parchment-dark border border-outline space-y-2" data-testid={`extra-quest-${qi}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase font-extrabold text-forest">Quest #{qi + 1}</span>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, extra_quests: (form.extra_quests || []).filter((_, i) => i !== qi) })}
                        className="text-xs text-compass font-bold hover:underline"
                        data-testid={`remove-extra-quest-${qi}`}
                      >
                        Rimuovi
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px]">Tipo</Label>
                        <Select
                          value={q.type}
                          onValueChange={(v) => {
                            const cs = [...form.extra_quests];
                            cs[qi] = { ...q, type: v };
                            setForm({ ...form, extra_quests: cs });
                          }}
                        >
                          <SelectTrigger data-testid={`extra-quest-${qi}-type`}><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="multiple">Scelta multipla</SelectItem>
                            <SelectItem value="single">Risposta singola</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Label className="text-[11px]">Domanda</Label>
                    <RichTextEditor
                      value={q.question || ""}
                      onChange={(html) => {
                        const cs = [...form.extra_quests];
                        cs[qi] = { ...q, question: html };
                        setForm({ ...form, extra_quests: cs });
                      }}
                      placeholder="Es. Cosa vedi in cima all'albero?"
                      testIdPrefix={`extra-quest-${qi}-question`}
                    />
                    {q.type === "multiple" ? (
                      <>
                        <Label className="text-[11px]">Opzioni (max 4) — clicca il numero per marcare la corretta</Label>
                        {(q.options || ["", "", "", ""]).slice(0, 4).map((opt, oi) => (
                          <div key={`eq-${qi}-o-${oi}`} className="flex gap-2 items-center">
                            <button
                              type="button"
                              onClick={() => {
                                const cs = [...form.extra_quests];
                                cs[qi] = { ...q, answer_index: oi };
                                setForm({ ...form, extra_quests: cs });
                              }}
                              className={`w-7 h-7 rounded-full font-bold text-xs flex items-center justify-center shrink-0 ${
                                Number(q.answer_index) === oi ? "bg-forest text-white" : "bg-white border-2 border-outline text-ink-700"
                              }`}
                              data-testid={`extra-quest-${qi}-correct-${oi}`}
                              title="Marca come risposta corretta"
                            >
                              {Number(q.answer_index) === oi ? "✓" : oi + 1}
                            </button>
                            <Input
                              value={opt}
                              onChange={(e) => {
                                const cs = [...form.extra_quests];
                                const opts = [...(q.options || ["", "", "", ""])];
                                opts[oi] = e.target.value;
                                cs[qi] = { ...q, options: opts };
                                setForm({ ...form, extra_quests: cs });
                              }}
                              placeholder={`Opzione ${oi + 1}`}
                              data-testid={`extra-quest-${qi}-option-${oi}`}
                            />
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        <Label className="text-[11px]">Risposta corretta (case-insensitive)</Label>
                        <Input
                          value={q.answer || ""}
                          onChange={(e) => {
                            const cs = [...form.extra_quests];
                            cs[qi] = { ...q, answer: e.target.value };
                            setForm({ ...form, extra_quests: cs });
                          }}
                          data-testid={`extra-quest-${qi}-answer`}
                        />
                      </>
                    )}
                    {/* Taccuino toggle */}
                    <div className="mt-2 p-2 rounded-xl bg-white border border-outline">
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={!!q.taccuino_enabled}
                          onChange={(e) => {
                            const cs = [...form.extra_quests];
                            cs[qi] = { ...q, taccuino_enabled: e.target.checked };
                            setForm({ ...form, extra_quests: cs });
                          }}
                          className="w-4 h-4 accent-forest"
                          data-testid={`extra-quest-${qi}-taccuino-toggle`}
                        />
                        <span className="font-bold text-ink-800">Aggiungi al taccuino</span>
                      </label>
                      {q.taccuino_enabled && (
                        <div className="mt-1.5">
                          <Label className="text-[11px]">Valore da salvare nel taccuino</Label>
                          <Input
                            value={q.taccuino_value || ""}
                            onChange={(e) => {
                              const cs = [...form.extra_quests];
                              cs[qi] = { ...q, taccuino_value: e.target.value };
                              setForm({ ...form, extra_quests: cs });
                            }}
                            placeholder="Es. Impronta di volpe · 42°N · parola-chiave"
                            data-testid={`extra-quest-${qi}-taccuino-value`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {(form.extra_quests || []).length === 0 && (
                  <p className="text-xs text-ink-500 italic mt-2">Nessuna quest extra. Clicca "+ Aggiungi quest" per aggiungerne.</p>
                )}
              </div>
            </div>
            <button onClick={save} className="btn-tactile mt-4 w-full" data-testid="admin-point-save">
              {editing ? "Salva modifiche" : "Crea tappa"}
            </button>
          </DialogContent>
        </Dialog>

        {/* Move-to-another-route dialog */}
        <Dialog open={!!movingPoint} onOpenChange={(v) => !v && setMovingPoint(null)}>
          <DialogContent className="rounded-3xl max-w-md" data-testid="admin-point-move-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Sposta tappa</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-ink-700">
              Sposta <strong>"{movingPoint?.name}"</strong> in un altro percorso. La tappa verrà aggiunta in coda.
            </p>
            {allRoutes.length === 0 ? (
              <div className="p-4 rounded-2xl bg-parchment-dark text-sm text-ink-500 text-center">
                Nessun altro percorso disponibile.
              </div>
            ) : (
              <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                {allRoutes.map((r) => (
                  <button
                    key={r.route_id}
                    onClick={() => movePointTo(r.route_id)}
                    className="w-full text-left p-3 rounded-2xl border-2 border-outline hover:border-gold hover:bg-gold-light transition-all flex items-center gap-3"
                    data-testid={`admin-point-move-target-${r.route_id}`}
                  >
                    <Map className="text-gold-hover shrink-0" size={20}/>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-ink-800 truncate">{r.title}</div>
                      <div className="text-xs text-ink-500">{r.city || "—"} · {r.status}</div>
                    </div>
                    <ArrowRightLeft size={16} className="text-ocean"/>
                  </button>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

// ---------- REWARDS ----------
function RewardsPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { name:"", description:"", image_url:"", emoji:"🏆", points_threshold:1000, active:true };
  const [form, setForm] = useState(blank);
  const load = () => api.get("/admin/rewards").then((r)=>setItems(r.data));
  useEffect(()=>{load();},[]);

  const save = async () => {
    const payload = { ...form, points_threshold: Number(form.points_threshold) };
    if (editing) await api.put(`/admin/rewards/${editing}`, payload);
    else await api.post("/admin/rewards", payload);
    setOpen(false); setEditing(null); setForm(blank); await load();
  };
  const del = async (r) => { if (!window.confirm("Cancellare?")) return; await api.delete(`/admin/rewards/${r.reward_id}`); await load(); };

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <div>
          <h2 className="font-display font-bold text-2xl">Livelli</h2>
          <p className="text-xs text-ink-500 mt-0.5">
            Badge sbloccati automaticamente al raggiungere una soglia di punti totali.
            Più livelli → più badge si accumulano nel profilo.
          </p>
        </div>
        <button onClick={()=>{setForm(blank);setEditing(null);setOpen(true);}} className="btn-tactile !py-2 !px-4 shrink-0" data-testid="admin-new-reward"><Plus size={16} className="inline"/> Nuovo livello</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((r) => (
          <Card key={r.reward_id} className="card-treasure p-3 flex gap-3" data-testid={`admin-level-${r.reward_id}`}>
            {r.image_url ? (
              <img
                src={r.image_url}
                alt=""
                className="w-16 rounded-xl bg-parchment-dark shrink-0"
                style={{ aspectRatio: "4 / 5", objectFit: "cover" }}
              />
            ) : (
              <div className="w-16 rounded-xl bg-gold-light shrink-0 flex items-center justify-center text-3xl" style={{ aspectRatio: "4 / 5" }}>
                {r.emoji || "🏆"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{r.emoji || "🏆"} {r.name}</div>
              <div className="text-xs text-ink-500 mt-0.5">Soglia: <strong className="text-gold-hover">{r.points_threshold} pt</strong></div>
              {!r.active && <div className="text-[10px] text-compass font-bold mt-0.5">Disattivato</div>}
              <div className="flex gap-2 mt-1">
                <button onClick={()=>{setEditing(r.reward_id);setForm({...blank, ...r});setOpen(true);}} className="text-gold-hover text-xs font-bold" data-testid={`admin-level-edit-${r.reward_id}`}><Edit2 size={12} className="inline"/></button>
                <button onClick={()=>del(r)} className="text-compass text-xs font-bold" data-testid={`admin-level-delete-${r.reward_id}`}><Trash2 size={12} className="inline"/></button>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="card-treasure p-6 text-center col-span-full text-ink-500 text-sm">
            Nessun livello configurato. Crea il primo per iniziare a premiare i tuoi utenti in base ai punti totali!
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-display">{editing?"Modifica":"Nuovo"} livello</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-[80px_1fr] gap-3">
              <div>
                <Label>Emoji</Label>
                <Input value={form.emoji || ""} onChange={(e)=>setForm({...form,emoji:e.target.value})} placeholder="🏆" maxLength={4} className="text-center text-2xl" data-testid="level-emoji-input"/>
              </div>
              <div>
                <Label>Nome livello</Label>
                <Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Es. Esploratore Bronzo" data-testid="level-name-input"/>
              </div>
            </div>
            <Label>Descrizione <span className="text-[10px] text-ink-500 font-normal">(mostrata all'utente sul badge)</span></Label>
            <Textarea value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} placeholder="Hai completato le prime cacce al tesoro. Continua così!" data-testid="level-description-input"/>
            <Label>Immagine badge <span className="text-[10px] text-ink-500 font-normal">(opzionale — se assente si usa l'emoji)</span></Label>
            <ImageUploader
              value={form.image_url}
              onChange={(url) => setForm({ ...form, image_url: url })}
              category="rewards"
              testIdPrefix="level-image"
            />
            <Label>Soglia punti totali per sbloccare</Label>
            <Input
              type="number"
              min="0"
              step="50"
              value={form.points_threshold}
              onChange={(e)=>setForm({...form,points_threshold:e.target.value})}
              placeholder="1000"
              data-testid="level-threshold-input"
            />
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="w-4 h-4 accent-forest"
                data-testid="level-active-toggle"
              />
              <span className="text-sm font-bold">Attivo (mostrato agli utenti)</span>
            </label>
          </div>
          <button onClick={save} className="btn-tactile mt-3" data-testid="level-save-btn">Salva livello</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- USERS / PAYMENTS ----------
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [seeding, setSeeding] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [fakeCount, setFakeCount] = useState(100);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    if (u.role === "admin") {
      if (!window.confirm(`⚠️ ATTENZIONE: stai per cancellare un ADMIN (${u.email}). Procedere?`)) return;
    } else {
      if (!window.confirm(`Cancellare l'utente ${u.email}? Tutti i suoi progress, taccuini, livelli sbloccati e pagamenti saranno rimossi. Operazione irreversibile.`)) return;
    }
    setBusyId(u.user_id);
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      setSelected((s) => { const n = new Set(s); n.delete(u.user_id); return n; });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore durante la cancellazione");
    } finally { setBusyId(null); }
  };

  const seedFakes = async () => {
    const n = Math.max(1, Math.min(500, parseInt(fakeCount) || 100));
    if (!window.confirm(`Generare ${n} utenti fake per popolare la classifica? Avranno email @odismap.com, nickname avatar casuali e punteggi 900–1400 (step 50). Potranno essere cancellati in qualsiasi momento.`)) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/users/seed-fakes", { count: n });
      toast.success(`${data.inserted} utenti fake creati`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione fake");
    } finally { setSeeding(false); }
  };

  const bulkDeleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Cancellare ${ids.length} utenti selezionati? Operazione irreversibile.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { user_ids: ids });
      toast.success(`${data.deleted} utenti cancellati`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore cancellazione bulk");
    } finally { setBulkBusy(false); }
  };

  const bulkDeleteAllFakes = async () => {
    if (!window.confirm("Cancellare TUTTI gli utenti fake creati per popolare la classifica? Gli utenti reali (registrati o Google) non vengono toccati. Operazione irreversibile.")) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { only_fakes: true });
      toast.success(`${data.deleted} utenti fake cancellati`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore cancellazione fake");
    } finally { setBulkBusy(false); }
  };

  const filtered = users.filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.nickname || "").toLowerCase().includes(q);
  });

  const toggleOne = (uid) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      const eligible = filtered.filter((u) => u.role !== "admin").map((u) => u.user_id);
      const allSelected = eligible.every((id) => s.has(id));
      if (allSelected) return new Set();
      const n = new Set(s);
      eligible.forEach((id) => n.add(id));
      return n;
    });
  };

  const fakeUsersCount = users.filter((u) => u.is_fake).length;

  return (
    <div className="space-y-3">
      {/* Toolbar: seed + bulk delete + search */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Cerca per nome, nickname o email..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-xs"
          data-testid="admin-users-search"
        />
        <div className="flex items-center gap-1 ml-auto flex-wrap">
          <Input
            type="number"
            min={1}
            max={500}
            value={fakeCount}
            onChange={(e) => setFakeCount(e.target.value)}
            className="w-20"
            data-testid="admin-fake-count-input"
            title="Numero di utenti fake da creare"
          />
          <button
            onClick={seedFakes}
            disabled={seeding}
            className="btn-tactile-sm bg-forest text-white px-3 py-2 rounded-xl font-bold text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="admin-seed-fakes-btn"
          >
            <Sparkles size={14}/> {seeding ? "Creazione..." : "Genera utenti fake"}
          </button>
          <button
            onClick={bulkDeleteSelected}
            disabled={bulkBusy || selected.size === 0}
            className="btn-tactile-sm bg-compass text-white px-3 py-2 rounded-xl font-bold text-xs inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="admin-bulk-delete-selected-btn"
          >
            <Trash2 size={14}/> Cancella selezionati ({selected.size})
          </button>
          {fakeUsersCount > 0 && (
            <button
              onClick={bulkDeleteAllFakes}
              disabled={bulkBusy}
              className="btn-tactile-sm bg-compass/80 text-white px-3 py-2 rounded-xl font-bold text-xs inline-flex items-center gap-1 disabled:opacity-40"
              data-testid="admin-delete-all-fakes-btn"
              title="Cancella tutti gli utenti fake"
            >
              <Trash2 size={14}/> Cancella tutti i fake ({fakeUsersCount})
            </button>
          )}
        </div>
      </div>
      <Card className="card-treasure overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-parchment-dark text-ink-700 text-xs uppercase tracking-widest">
            <tr>
              <th className="p-3 w-8">
                <input
                  type="checkbox"
                  onChange={toggleAll}
                  checked={filtered.length > 0 && filtered.filter((u) => u.role !== "admin").every((u) => selected.has(u.user_id))}
                  data-testid="admin-users-select-all"
                />
              </th>
              <th className="text-left p-3">Nickname</th>
              <th className="text-left p-3">Nome</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Ruolo</th>
              <th className="text-right p-3">Punti</th>
              <th className="text-right p-3 w-24">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.user_id} className={`border-t border-outline ${u.is_fake ? "bg-parchment-dark/40" : ""}`} data-testid={`admin-user-row-${u.user_id}`}>
                <td className="p-3">
                  <input
                    type="checkbox"
                    disabled={u.role === "admin"}
                    checked={selected.has(u.user_id)}
                    onChange={() => toggleOne(u.user_id)}
                    data-testid={`admin-user-check-${u.user_id}`}
                  />
                </td>
                <td className="p-3 font-bold">
                  {u.nickname || "—"}
                  {u.is_fake && <span className="ml-2 px-1.5 py-0.5 rounded bg-gold-light text-ink-800 text-[10px] font-bold uppercase tracking-widest">fake</span>}
                </td>
                <td className="p-3 text-ink-700">{u.name}</td>
                <td className="p-3 text-ink-700">{u.email}</td>
                <td className="p-3"><span className="px-2 py-0.5 rounded-full bg-gold-light text-ink-800 font-bold text-xs">{u.role}</span></td>
                <td className="p-3 text-right font-display font-extrabold text-gold-hover">{u.total_points}</td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => del(u)}
                    disabled={busyId === u.user_id}
                    className="text-compass hover:text-compass/70 font-bold text-xs disabled:opacity-40 inline-flex items-center gap-1"
                    title="Cancella utente e tutti i suoi dati"
                    data-testid={`admin-user-delete-${u.user_id}`}
                  >
                    <Trash2 size={14}/> {busyId === u.user_id ? "..." : "Elimina"}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-ink-500 text-sm">Nessun utente trovato.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function PaymentsPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((r)=>setItems(r.data)); }, []);
  return (
    <Card className="card-treasure overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-parchment-dark text-ink-700 text-xs uppercase tracking-widest">
          <tr><th className="text-left p-3">Sessione</th><th className="text-left p-3">User</th><th className="text-left p-3">Percorso</th><th className="text-right p-3">€</th><th className="text-left p-3">Stato</th></tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.transaction_id} className="border-t border-outline">
              <td className="p-3 text-xs">{t.session_id.slice(0,18)}…</td>
              <td className="p-3 text-xs">{t.user_id.slice(0,8)}…</td>
              <td className="p-3 text-xs">{t.route_id.slice(0,8)}…</td>
              <td className="p-3 text-right font-bold">€{t.amount}</td>
              <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${t.payment_status==="paid"?"bg-forest text-white":"bg-parchment-dark text-ink-700"}`}>{t.payment_status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ---- COMPETITIONS ----
function CompetitionsPanel() {
  const [items, setItems] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { name:"", route_id:"", type:"solo", family_id:null, time_bonus_minutes:60, time_bonus_pct:20, status:"active" };
  const [form, setForm] = useState(blank);

  const load = () => api.get("/admin/competitions").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/routes").then((r) => setRoutes(r.data));
  }, []);

  const save = async () => {
    const payload = { ...form, time_bonus_minutes:Number(form.time_bonus_minutes), time_bonus_pct:Number(form.time_bonus_pct) };
    if (editing) await api.put(`/admin/competitions/${editing}`, payload);
    else await api.post("/admin/competitions", payload);
    setOpen(false); setEditing(null); setForm(blank); await load();
  };
  const del = async (c) => { if (!window.confirm("Cancellare gara?")) return; await api.delete(`/admin/competitions/${c.competition_id}`); await load(); };

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <h2 className="font-display font-bold text-2xl">Gare</h2>
        <button onClick={()=>{setForm(blank);setEditing(null);setOpen(true);}} className="btn-tactile !py-2 !px-4" data-testid="admin-new-competition">
          <Plus size={16} className="inline"/> Nuova
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((c) => {
          const route = routes.find((r) => r.route_id === c.route_id);
          return (
            <Card key={c.competition_id} className="card-treasure p-4 flex gap-3" data-testid={`admin-comp-${c.competition_id}`}>
              <div className="w-12 h-12 rounded-2xl bg-compass flex items-center justify-center"><Zap className="text-white" size={22}/></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-ink-900 truncate">{c.name}</div>
                <div className="text-xs text-ink-500">{route?.title} · bonus +{c.time_bonus_pct}% in {c.time_bonus_minutes}min</div>
                <div className="text-[10px] mt-1"><span className={`px-2 py-0.5 rounded-full font-bold ${c.status==="active"?"bg-forest text-white":"bg-parchment-dark text-ink-700"}`}>{c.status}</span> · <span>{c.type}</span></div>
                <div className="flex gap-2 mt-2">
                  <button onClick={()=>{setEditing(c.competition_id);setForm({...c});setOpen(true);}} className="text-xs font-bold text-gold-hover"><Edit2 size={12} className="inline"/></button>
                  <button onClick={()=>del(c)} className="text-xs font-bold text-compass"><Trash2 size={12} className="inline"/></button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-display">{editing?"Modifica":"Nuova"} gara</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Nome</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="comp-form-name"/>
            <Label>Percorso</Label>
            <Select value={form.route_id} onValueChange={(v)=>setForm({...form,route_id:v})}>
              <SelectTrigger data-testid="comp-form-route"><SelectValue placeholder="Seleziona..."/></SelectTrigger>
              <SelectContent>
                {routes.map((r) => <SelectItem key={r.route_id} value={r.route_id}>{r.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Tipo</Label>
                <Select value={form.type} onValueChange={(v)=>setForm({...form,type:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solo">Solo</SelectItem>
                    <SelectItem value="family">Famiglia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Bonus min</Label><Input type="number" value={form.time_bonus_minutes} onChange={(e)=>setForm({...form,time_bonus_minutes:e.target.value})}/></div>
              <div><Label>Bonus %</Label><Input type="number" value={form.time_bonus_pct} onChange={(e)=>setForm({...form,time_bonus_pct:e.target.value})}/></div>
            </div>
            <Label>Stato</Label>
            <Select value={form.status} onValueChange={(v)=>setForm({...form,status:v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">In arrivo</SelectItem>
                <SelectItem value="active">Attiva</SelectItem>
                <SelectItem value="ended">Terminata</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button onClick={save} className="btn-tactile mt-3" data-testid="comp-form-save">Salva</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- PROOFS ----
function ProofsPanel() {
  const [items, setItems] = useState([]);
  const [zoom, setZoom] = useState(null);
  const load = () => api.get("/admin/proofs").then((r) => setItems(r.data));
  useEffect(()=>{load();},[]);

  const review = async (p, approved) => {
    await api.post(`/admin/proofs/${p.proof_id}/review?approved=${approved}`);
    await load();
  };
  if (!items.length) return <Card className="card-treasure p-8 text-center text-ink-500">Nessuna foto-prova caricata</Card>;
  return (
    <>
      <div className="mb-4 p-3 rounded-2xl bg-ocean-light border border-ocean/30 text-sm text-ink-800">
        🤖 <strong>AI Review automatica:</strong> Gemini 3 Flash analizza ogni foto.
        Se confidence ≥ 60% viene <span className="text-forest font-bold">approvata</span>,
        altrimenti <span className="text-compass font-bold">rifiutata</span>. Puoi sovrascrivere a mano.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((p) => {
          const conf = p.ai_confidence;
          const aiColor = conf == null ? "bg-parchment-dark text-ink-700" : conf >= 60 ? "bg-forest text-white" : "bg-compass text-white";
          return (
            <Card key={p.proof_id} className="card-treasure overflow-hidden p-0" data-testid={`proof-${p.proof_id}`}>
              <button onClick={()=>setZoom(p)} className="block w-full">
                <img src={p.photo_base64} alt="prova" className="w-full h-40 object-cover"/>
              </button>
              <div className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-bold text-ink-800 truncate">{p.user_name}</div>
                  {conf != null && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${aiColor}`} data-testid={`ai-conf-${p.proof_id}`}>
                      🤖 {conf}%
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-ink-500 truncate">{p.point_name || ""}</div>
                <div className="text-[10px] text-ink-500 truncate">{p.created_at?.slice(0,16).replace("T"," ")}</div>
                {p.ai_reasoning && (
                  <div className="mt-1 text-[10px] text-ink-700 italic line-clamp-2" title={p.ai_reasoning}>"{p.ai_reasoning}"</div>
                )}
                {p.reviewed ? (
                  <div className={`text-[10px] mt-1.5 font-bold ${p.approved ? "text-forest" : "text-compass"}`}>
                    {p.approved ? "✓ Approvata" : "✗ Rifiutata"}
                  </div>
                ) : (
                  <div className="text-[10px] mt-1.5 font-bold text-ink-500">In attesa AI...</div>
                )}
                <div className="flex gap-1 mt-2">
                  <button onClick={()=>review(p,true)} className="flex-1 text-[10px] bg-forest/15 text-forest rounded-lg py-1 font-bold hover:bg-forest hover:text-white"><CheckCircle2 size={11} className="inline"/> Sì</button>
                  <button onClick={()=>review(p,false)} className="flex-1 text-[10px] bg-compass/15 text-compass rounded-lg py-1 font-bold hover:bg-compass hover:text-white"><Trash2 size={11} className="inline"/> No</button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      <Dialog open={!!zoom} onOpenChange={()=>setZoom(null)}>
        <DialogContent className="rounded-3xl max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Foto-prova di {zoom?.user_name} · {zoom?.point_name}</DialogTitle></DialogHeader>
          {zoom && (
            <>
              <img src={zoom.photo_base64} alt="" className="w-full rounded-2xl"/>
              {zoom.ai_confidence != null && (
                <div className="mt-3 p-3 rounded-2xl bg-ocean-light text-sm">
                  <strong>🤖 AI ({zoom.ai_confidence}% confidence)</strong>: {zoom.ai_reasoning || "—"}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r)=>setStats(r.data)).catch(()=>{}); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-ink-900 flex items-center justify-center shadow-tactile">
          <Shield className="text-gold" size={26}/>
        </div>
        <div>
          <h1 className="font-display font-extrabold text-3xl text-ink-900">Admin</h1>
          <p className="text-ink-500 text-sm">Gestisci tutta l'avventura</p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatTile label="Utenti" value={stats.users} icon={Users}/>
          <StatTile label="Percorsi" value={stats.routes} icon={Map}/>
          <StatTile label="Punti" value={stats.points} icon={MapPin}/>
          <StatTile label="Livelli" value={stats.rewards} icon={Gift}/>
          <StatTile label="Completati" value={stats.completed_routes} icon={Sparkles}/>
          <StatTile label="Pagamenti" value={stats.payments} icon={CreditCard}/>
        </div>
      )}

      <Tabs defaultValue="routes">
        <TabsList className="bg-parchment-dark rounded-2xl p-1 flex-wrap h-auto">
          <TabsTrigger value="routes" className="rounded-xl data-[state=active]:bg-white" data-testid="admin-tab-routes">Percorsi</TabsTrigger>
          <TabsTrigger value="rewards" className="rounded-xl data-[state=active]:bg-white" data-testid="admin-tab-rewards">Livelli</TabsTrigger>
          <TabsTrigger value="competitions" className="rounded-xl data-[state=active]:bg-white" data-testid="admin-tab-competitions"><Zap size={14} className="inline mr-1"/>Gare</TabsTrigger>
          <TabsTrigger value="proofs" className="rounded-xl data-[state=active]:bg-white" data-testid="admin-tab-proofs"><Camera size={14} className="inline mr-1"/>Foto-prove</TabsTrigger>
          <TabsTrigger value="users" className="rounded-xl data-[state=active]:bg-white" data-testid="admin-tab-users">Utenti</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-xl data-[state=active]:bg-white" data-testid="admin-tab-payments">Pagamenti</TabsTrigger>
          <TabsTrigger value="codes" className="rounded-xl data-[state=active]:bg-white" data-testid="admin-tab-codes"><Ticket size={14} className="inline mr-1"/>Codici</TabsTrigger>
        </TabsList>
        <TabsContent value="routes" className="mt-6"><RoutesPanel/></TabsContent>
        <TabsContent value="rewards" className="mt-6"><RewardsPanel/></TabsContent>
        <TabsContent value="competitions" className="mt-6"><CompetitionsPanel/></TabsContent>
        <TabsContent value="proofs" className="mt-6"><ProofsPanel/></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersPanel/></TabsContent>
        <TabsContent value="payments" className="mt-6"><PaymentsPanel/></TabsContent>
        <TabsContent value="codes" className="mt-6"><CodesPanel/></TabsContent>
      </Tabs>
    </div>
  );
}
